import { type FC, useCallback } from "react";

import { useQuery } from "@apollo/client";
import { Button } from "@snapchat/snap-design-system";
import { type GraphQLError } from "graphql";

import { useModal } from "../../../camkit/core/hooks";
import { getHostedLens } from "../../../hosted-lenses/api/query/getHostedLens";
import {
    type GetHostedLensQuery,
    type GetHostedLensQueryVariables,
} from "../../../hosted-lenses/api/query/getHostedLens.gql-generated";
import { HostedLensDisclaimerModal } from "../../../hosted-lenses/components/HostedLensDisclaimerModal";
import {
    HostedLensDisclaimerEnum,
    type HostedLensDisclaimerOptions,
} from "../../../hosted-lenses/components/disclaimerOptions";
import { HostedLensDisabledTooltipText } from "../../../hosted-lenses/components/messages.generated";
import { useDisclaimerAndAcceptance } from "../../../hosted-lenses/hooks/useDisclaimerAndAcceptance";
import { useHostedLens } from "../../../hosted-lenses/hooks/useHostedLens";
import { ButtonDisabledTooltip } from "../../../shared/components/ButtonDisabledTooltip";
import { copyToClipboard } from "../../../shared/utils/browser";
import { TemporaryTooltip } from "../shared";

import { shareLensButton } from "./LensHeader.styles";

interface ShareHostedLensButtonProps {
    orgId: string;
    lensId: string;
    isDisabled?: boolean;
}

export const ShareHostedLensButton: FC<ShareHostedLensButtonProps> = ({ orgId, lensId, isDisabled }) => {
    const { createHostedLens, isUpdating } = useHostedLens();
    const [showDisclaimerProps, showDisclaimerModal, option] = useModal<HostedLensDisclaimerOptions>();
    const { handleErrors } = useDisclaimerAndAcceptance({
        option,
        showDisclaimerModal,
    });

    const { data, loading } = useQuery<GetHostedLensQuery, GetHostedLensQueryVariables>(getHostedLens, {
        variables: {
            organizationId: orgId,
            lensID: lensId,
        },
        errorPolicy: "all",
        fetchPolicy: "network-only",
    });

    const onClick = useCallback(() => {
        if (data?.getHostedLens.lens) {
            copyToClipboard(data.getHostedLens.lens.link);
        } else {
            showDisclaimerModal(HostedLensDisclaimerEnum.GENERATE);
        }
    }, [data, showDisclaimerModal]);

    const onModalAcceptance = useCallback(
        async (isAccepted: boolean) => {
            if (isAccepted) {
                try {
                    const res = await createHostedLens({
                        variables: { lensID: lensId },
                        refetchQueries: [getHostedLens],
                    });
                    if (res.errors) handleErrors(res.errors);
                    if (res.data?.generateHostedLens.lens) {
                        copyToClipboard(res.data.generateHostedLens.lens.link);
                    }
                } catch (error) {
                    handleErrors([error as GraphQLError]);
                }
            }
        },
        [createHostedLens, handleErrors, lensId],
    );

    return (
        <>
            {isDisabled ? (
                <ButtonDisabledTooltip
                    isDisabled={true}
                    tooltip={{
                        content: <HostedLensDisabledTooltipText />,
                    }}
                    className={shareLensButton}
                    type="default"
                >
                    Share WebAR Lens
                </ButtonDisabledTooltip>
            ) : (
                <TemporaryTooltip content="Hosted WebAR Lens URL copied to clipboard" callbackAction={onClick}>
                    <Button type="default" className={shareLensButton} isPending={loading}>
                        Share WebAR Lens
                    </Button>
                </TemporaryTooltip>
            )}
            <HostedLensDisclaimerModal
                {...showDisclaimerProps}
                loading={isUpdating}
                option={option}
                onAction={onModalAcceptance}
            />
        </>
    );
};
